/**
 * Session Keep-Alive Utility
 *
 * Provides functionality to keep user sessions alive by periodically
 * pinging the server. This prevents sessions from being cleaned up
 * as stale when users leave their browser open overnight.
 */

import { getApi } from './api-injector.js';

/**
 * Sends a keep-alive ping to the server.
 * This updates the session's LastActivity timestamp, preventing
 * the session from being marked as stale.
 *
 * @returns Promise that resolves when the ping succeeds
 */
export async function sendKeepAlive(): Promise<void> {
  try {
    const api = getApi();
    await api.get<unknown>('api/Test/KeepAlive', true);
  } catch {
    // Silently ignore — session may have expired or user logged out.
  }
}

/** Keep-alive interval in milliseconds (1 hour) */
const KEEP_ALIVE_INTERVAL_MS = 60 * 60 * 1000;

let keepAliveTimerId: ReturnType<typeof setInterval> | null = null;

/**
 * Starts the session keep-alive timer.
 * Sends a ping to the server every hour to prevent session expiration.
 *
 * This should be called after successful login.
 */
export function startSessionKeepAlive(): void {
  // Don't start multiple timers
  if (keepAliveTimerId !== null) return;

  keepAliveTimerId = setInterval(async () => {
    await sendKeepAlive();
  }, KEEP_ALIVE_INTERVAL_MS);

  console.log('Session keep-alive timer started (1 hour interval)');
}

/**
 * Stops the session keep-alive timer.
 *
 * This should be called during logout/clear.
 */
export function stopSessionKeepAlive(): void {
  if (keepAliveTimerId !== null) {
    clearInterval(keepAliveTimerId);
    keepAliveTimerId = null;
    console.log('Session keep-alive timer stopped');
  }
}
